/**
 * ForgotPasswordPanel.jsx — Panel FORGOT_PASSWORD.
 *
 * Flow :
 *   1. Affiche un champ email + bouton "Send reset link".
 *   2. Submit → POST /auth/reset-password (public, safe-enum, rate-limited).
 *   3. Sur succès (toujours 200 côté serveur — safe enumeration), dispatch RESET_LINK_SENT
 *      → bascule l'UI en mode confirmation "Check your email".
 *   4. Le lien dans l'email pointe vers /confirm-password?token=… (page existante, déjà routée).
 *
 * Règles DA :
 *  §14 — Zéro import @mui.
 *  §20 — Tokens via theme.
 *  §12 undecies — Casse grammaticale.
 *  §8.7 — Zéro Redux.
 *
 * Props consommées :
 *   resetLinkSent {boolean}  — passé depuis EarniWalletModal (smState.resetLinkSent).
 *   dispatch      {function} — dispatch useWalletStateMachine.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTION_TYPES } from '../hooks/useWalletStateMachine';
import * as AuthService from '../../../services/AuthService';
import {
  WalletBody,
  WalletFooter,
  FormGroup,
  FormLabel,
  FormInput,
  InlineError,
  PrimaryBtn,
  BackLink,
  ToSText,
} from '../styles/EarniWalletModal.styles';
import { Spinner } from '../../ui/ModalDA';
import { COLORS } from '../../ui/theme';

const BackArrow = () => (
  <svg width="0.875rem" height="0.875rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="m12 5-7 7 7 7" />
  </svg>
);

const CheckIcon = () => (
  <svg width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" stroke={COLORS.cta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordPanel({ resetLinkSent, dispatch }) {
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const handleBackToLogin = useCallback(() => {
    dispatch({ type: ACTION_TYPES.SWITCH_TO_LOGIN });
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const trimmed = (email || '').trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setError(t('InvalidEmailFormat'));
      return;
    }
    setLoading(true);
    try {
      await AuthService.resetPassword({ email: trimmed });
      // Backend renvoie toujours 200 (safe enumeration). On bascule l'UI vers confirmation.
      dispatch({ type: ACTION_TYPES.RESET_LINK_SENT });
    } catch (err) {
      // Rate-limit 429 ou erreur réseau : on affiche un message générique.
      const msg = err?.response?.data?.message || err?.message || t('ResetLinkError');
      setError(typeof msg === 'string' ? msg : t('ResetLinkError'));
    } finally {
      setLoading(false);
    }
  }, [email, dispatch, t]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);

  /* ── Mode confirmation : email envoyé ─────────────────────────────────────── */
  if (resetLinkSent) {
    return (
      <>
        <WalletBody>
          <BackLink
            type="button"
            onClick={handleBackToLogin}
            aria-label={t('BackToSignIn')}
          >
            <BackArrow />
            {t('BackToSignIn')}
          </BackLink>

          <div style={{ textAlign: 'center', padding: '0.5rem 0 0.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
              <CheckIcon />
            </div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--ev-ink)', margin: '0 0 0.5rem' }}>
              {t('ResetLinkSentTitle')}
            </h3>
            <p style={{ fontSize: '0.9375rem', color: 'var(--ev-muted)', lineHeight: 1.6, margin: 0 }}>
              {t('ResetLinkSentBody')}
            </p>
          </div>
        </WalletBody>

        <WalletFooter>
          <PrimaryBtn type="button" onClick={handleBackToLogin}>
            {t('BackToSignIn')}
          </PrimaryBtn>
        </WalletFooter>
      </>
    );
  }

  /* ── Mode saisie : formulaire email ───────────────────────────────────────── */
  return (
    <form
      noValidate
      onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
      aria-label={t('ForgotPasswordForm')}
    >
      <WalletBody>
        <BackLink
          type="button"
          onClick={handleBackToLogin}
          aria-label={t('BackToSignIn')}
        >
          <BackArrow />
          {t('BackToSignIn')}
        </BackLink>

        <p style={{ fontSize: '0.9375rem', color: 'var(--ev-muted)', lineHeight: 1.6, margin: '0 0 1rem' }}>
          {t('ForgotPasswordBody')}
        </p>

        <FormGroup>
          <FormLabel htmlFor="forgotEmail">{t('EmailAddress')}</FormLabel>
          <FormInput
            id="forgotEmail"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
            placeholder={t('EmailPlaceholder')}
            $hasError={!!error}
            autoComplete="email"
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </FormGroup>

        {error && (
          <InlineError role="alert" aria-live="polite" style={{ marginTop: '0.5rem' }}>
            {error}
          </InlineError>
        )}

        <PrimaryBtn
          type="submit"
          disabled={loading || !email}
          style={{ marginTop: '1.25rem' }}
          aria-busy={loading}
        >
          {loading ? <Spinner style={{ marginBottom: 0, width: '1.25rem', height: '1.25rem' }} /> : null}
          {loading ? t('SendingResetLink') : t('SendResetLink')}
        </PrimaryBtn>
      </WalletBody>

      <WalletFooter>
        <ToSText>
          By continuing, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">terms of use</a>.
        </ToSText>
      </WalletFooter>
    </form>
  );
}

export default ForgotPasswordPanel;
